import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { analyticsApi } from '../services/analytics-api.service';
import type { EngagementMetricsQueryParams } from '../types/engagement-metrics.types';

const DEFAULT_PAGE_SIZE = 25;

export const dormantProvidersKeys = {
  all: ['analytics', 'engagement-metrics', 'dormant-providers'] as const,
  list: (params?: EngagementMetricsQueryParams) =>
    [...dormantProvidersKeys.all, 'list', params] as const,
};

/**
 * Server-side pagination for the Overview "Providers Needing Attention" table, decoupled from
 * the combined `useEngagementMetricsData` fetch so paging doesn't refetch the whole dashboard.
 * The table's own category/specialty narrowing (on top of `baseParams`' global filters) resets
 * to page 1, same as changing page size.
 */
export const useDormantProvidersData = (
  baseParams: EngagementMetricsQueryParams,
  isActive = true,
) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [categoryName, setCategoryName] = useState('');
  const [specialtyName, setSpecialtyName] = useState('');

  const queryParams = useMemo<EngagementMetricsQueryParams>(
    () => ({
      ...baseParams,
      page,
      pageSize,
      dormantCategoryName: categoryName || undefined,
      dormantSpecialtyName: specialtyName || undefined,
    }),
    [baseParams, page, pageSize, categoryName, specialtyName],
  );

  const query = useQuery({
    queryKey: dormantProvidersKeys.list(queryParams),
    queryFn: async () => {
      const response = await analyticsApi.getDormantProviders(queryParams);
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: isActive,
  });

  const setCategoryFilter = (value: string) => {
    setCategoryName(value);
    setPage(1);
  };

  const setSpecialtyFilter = (value: string) => {
    setSpecialtyName(value);
    setPage(1);
  };

  const setPageSizeAndReset = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  return {
    items: query.data?.items ?? [],
    meta: query.data?.meta,
    filterOptions: query.data?.filterOptions ?? { categoryNames: [], specialtyNames: [] },
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    page,
    setPage,
    pageSize,
    setPageSize: setPageSizeAndReset,
    categoryName,
    setCategoryName: setCategoryFilter,
    specialtyName,
    setSpecialtyName: setSpecialtyFilter,
  };
};
